import { createStore } from 'vuex';
import analytics from './modules/analytics';
import { proxyUrl, apiUrl, username, password } from '/config';
import ApiClient from '@/ApiClient';
import { mapLocale } from '@/utils/localeMapper';
import { FilterItemsContext } from '@/utils/FilterItemsContext';
import { FilterStatusHistoryContext } from '@/utils/FilterStatusHistoryContext';
import { DuckDBService } from '@/utils/DuckDBService';

const apiClient = new ApiClient(proxyUrl);
const duckDBService = new DuckDBService();

const staticState = {
  items: [], // Keep this for compatibility, but we'll primarily use DuckDB
  drawings: [],
  drawingsObj: {},
  categories: [],
  categoriesObj: {},
  itemTemplatesObj: {},
  statusTemplatesObj: {},
  formTemplatesObj: {},
  drawingLocationsObj: {},
  locationsObj: {},
  teamsObj: {},
  teamUsersObj: {},
  userTeamsObj: {},
  usersObj: {},
  permissionRulesObj: {},
};

const store = createStore({
  modules: {
    analytics,
  },
  state: {
    apiClient,
    duckDBService: duckDBService,
    filterItemsContext: new FilterItemsContext(duckDBService),
    filterStatusHistoryContext: new FilterStatusHistoryContext(duckDBService),
    isDuckDBInitialized: false,
    isDataLoaded: false,
    settings: {
      proxyUrl: proxyUrl,
      apiUrl: apiUrl,
      logoUrl: '',
      projectCode: null,
      projectName: null,
      scopedToken: null,
      username: username,
      password: password,
      selectedProject: null,
      userId: null,
      locale: 'defaultLocale',
      timeZone: 'UTC',
      showOpenItemsOnly: false,
    },
    showWelcomeDialog: true,
    loadDashboard: false,
    dataLoaded: false,
    // Tasks tab: true once the project has a default schedule and its derived tables were built
    tasksAvailable: false,
    selectedItems: [],
    selectedTeam: 'Un-Assigned',
    statusFilters: {},
    itemTypeFilters: {},
    theme: 'lightTheme',
    buildMode: 'production',
    static: staticState,
  },
  mutations: {
    setDuckDBInitialized(state, status) {
      state.isDuckDBInitialized = status;
    },
    setSettings(state, newSettings) {
      state.settings = { ...state.settings, ...newSettings };
    },
    setTheme(state, newTheme) {
      state.theme = newTheme;
    },
    updateSettingsField(state, { field, value }) {
      state.settings[field] = value;
    },
    setApiUrl(state, apiUrl) {
      state.apiClient.apiUrl = apiUrl;
    },
    setApiToken(state, token) {
      state.apiClient.setToken(token);
      state.settings.scopedToken = token;
    },
    setUserId(state, value) {
      state.settings.userId = value;
    },
    setProjectCode(state, value) {
      state.apiClient.setProjectCode(value);
      state.settings.projectCode = value;
    },
    setProjectName(state, value) {
      state.settings.projectName = value;
    },
    setScopedToken(state, value) {
      state.settings.scopedToken = value;
    },
    setDataLoaded(state, status) {
      state.duckDBService.isDataLoaded = status;
      state.isDataLoaded = status;
    },
    setTasksAvailable(state, available) {
      state.tasksAvailable = available;
    },
    setItems(state, items) {
      state.static.items = Object.freeze([...items]);
    },
    setCategories(state, categories) {
      state.static.categories = Object.freeze(categories);
    },
    setCategoriesObj(state, categoriesObj) {
      state.static.categoriesObj = Object.freeze(categoriesObj);
    },
    setItemTemplatesObj(state, itemTemplatesObj) {
      state.static.itemTemplatesObj = Object.freeze(itemTemplatesObj);
    },
    setStatusTemplatesObj(state, statusTemplatesObj) {
      state.static.statusTemplatesObj = Object.freeze(statusTemplatesObj);
    },
    setFormTemplatesObj(state, formTemplatesObj) {
      state.static.formTemplatesObj = Object.freeze(formTemplatesObj);
    },
    setDrawings(state, drawings) {
      state.static.drawings = Object.freeze(drawings);
    },
    setDrawingsObj(state, drawingsObj) {
      state.static.drawingsObj = Object.freeze(drawingsObj);
    },
    setDrawingLocationsObj(state, drawingLocationsObj) {
      state.static.drawingLocationsObj = Object.freeze(drawingLocationsObj);
    },
    setPermissionRulesObj(state, permissionRulesObj) {
      state.static.permissionRulesObj = Object.freeze(permissionRulesObj);
    },
    setLocationsObj(state, locationsObj) {
      state.static.locationsObj = Object.freeze(locationsObj);
    },
    setTeamsObj(state, teamsObj) {
      state.static.teamsObj = Object.freeze(teamsObj);
    },
    setTeamUsersObj(state, teamUsersObj) {
      state.static.teamUsersObj = Object.freeze(teamUsersObj);
    },
    setUserTeamsObj(state, userTeamsObj) {
      state.static.userTeamsObj = Object.freeze(userTeamsObj);
    },
    setUsersObj(state, usersObj) {
      state.static.usersObj = Object.freeze(usersObj);
    },
    setSelectedItems(state, selectedItems) {
      state.selectedItems = selectedItems;
    },
    setSelectedTeam(state, selectedTeam) {
      state.selectedTeam = selectedTeam;
    },
    setStatusFilters(state, filters) {
      state.statusFilters = { ...filters };
    },
    setItemTypeFilters(state, filters) {
      state.itemTypeFilters = { ...filters };
    },
    setValues(state, values) {
      state.settings.apiUrl = values.apiUrl;
      state.settings.projectCode = values.projectCode;
      state.settings.projectName = values.projectName;
      state.settings.scopedToken = values.scopedToken;
      state.settings.locale = values.locale;
      state.theme = values.theme;
      state.apiClient.setApiUrl(values.apiUrl);
      state.apiClient.setProjectCode(values.projectCode);
      state.apiClient.setScopedToken(values.scopedToken);
    },
    setLocale(state, locale) {
      state.settings.locale = locale;
    },
    setLoadDashboard(state, loadDashboard) {
      state.loadDashboard = loadDashboard;
    },
    setShowWelcomeDialog(state, showWelcomeDialog) {
      state.showWelcomeDialog = showWelcomeDialog;
    },
    setBuildMode(state, buildMode) {
      state.buildMode = buildMode;
    },
    setLogoUrl(state, logoUrl) {
      state.settings.logoUrl = logoUrl;
    },
    setTimeZone(state, timeZone) {
      state.settings.timeZone = timeZone;
    },
    setShowOpenItemsOnly(state, mode) {
      state.settings.showOpenItemsOnly = mode;
    },
    setFilterItemsContext(state, filterContext) {
      state.filterItemsContext = filterContext;
    },
    updateFilter(state, { chartId, ids }) {
      state.filterItemsContext.updateFilter(chartId, ids);
    },
    removeFilter(state, chartId) {
      state.filterItemsContext.removeFilter(chartId);
    },
    resetFilters(state) {
      state.filterItemsContext.resetFilters();
    },
    setFilterStatusHistoryContext(state, filterStatusHistoryContext) {
      state.filterStatusHistoryContext = filterStatusHistoryContext;
    },
    updateStatusHistoryFilter(state, { chartId, ids }) {
      state.filterStatusHistoryContext.updateFilter(chartId, ids);
    },
    removeStatusHistoryFilter(state, chartId) {
      state.filterStatusHistoryContext.removeFilter(chartId);
    },
    resetStatusHistoryFilters(state) {
      state.filterStatusHistoryContext.resetFilters();
    },
  },
  actions: {
    async initializeDuckDB({ commit, state }, { proxyUrl, apiUrl, scopedToken }) {
      try {
        const success = await state.duckDBService.initialize({ proxyUrl, apiUrl, scopedToken });
        commit('setDuckDBInitialized', success);
        return { success };
      } catch (error) {
        console.error("Error initializing DuckDB:", error);
        commit('setDuckDBInitialized', false);
        return { success: false, error };
      }
    },
    async loadLookupObjectsFromDB({ commit, state }) {
      try {
        const teamsResult = await state.duckDBService.conn.query('SELECT * FROM teams');
        const teamsObj = {};
        teamsResult.toArray().forEach(row => {
          const team = row.toJSON();
          const id = team.Id || team.id;
          if (id) {
            teamsObj[id] = {
              id: id,
              name: team.Name || team.name || '',
              teamProfileTitle: team.TeamProfileTitle || team.teamProfileTitle || ''
            };
          }
        });
        commit('setTeamsObj', teamsObj);
      } catch (error) {
        console.error('Error loading teams lookup from DB:', error);
      }

      try {
        // Schema: Key (team ID) | Value (JSON array of users)
        const usersResult = await state.duckDBService.conn.query('SELECT Key, Value FROM users');
        const usersObj = {};
        usersResult.toArray().forEach(row => {
          const rowData = row.toJSON();
          let teamUsers = rowData.Value || rowData.value;

          if (typeof teamUsers === 'string') {
            try {
              teamUsers = JSON.parse(teamUsers);
            } catch (e) {
              teamUsers = [];
            }
          }

          if (Array.isArray(teamUsers)) {
            teamUsers.forEach(u => {
              const id = u.Id || u.id;
              if (id && !usersObj[id]) {
                usersObj[id] = {
                  displayName: u.DisplayName || u.displayName || '',
                  avatarUrl: u.AvatarUrl || u.avatarUrl || ''
                };
              }
            });
          }
        });
        commit('setUsersObj', usersObj);
      } catch (error) {
        console.error('Error loading users lookup from DB:', error);
      }
    },
    initializeFilterContext({ commit, state }) {
      const filterContext = new FilterItemsContext(state.duckDBService);
      filterContext.initializeFilterContext();
      commit('setFilterItemsContext', filterContext);
    },
    initializeStatusHistoryFilterContext({ commit, state }) {
      const filterStatusHistoryContext = new FilterStatusHistoryContext(state.duckDBService);
      filterStatusHistoryContext.initializeFilterContext();
      commit('setFilterStatusHistoryContext', filterStatusHistoryContext);
    },
    saveSettings({ commit }, settings) {
      commit('setSettings', settings);
    },
    updateSettingsField({ commit }, payload) {
      commit('updateSettingsField', payload);
    },
    updateApiUrl({ commit }, apiUrl) {
      commit('setApiUrl', apiUrl);
    },
    updateProjectCode({ commit }, projectCode) {
      commit('setProjectCode', projectCode);
    },
    updateApiProjectName({ commit }, projectName) {
      commit('setProjectName', projectName);
    },
    updateUserId({ commit }, userId) {
      commit('setUserId', userId);
    },
    updateItems({ commit }, updatedItems) {
      commit('setItems', updatedItems);
    },
    updateValues({ commit, dispatch }, values) {
      commit('setValues', values);
      dispatch('analytics/initializeAnalytics');
    },
    updateTheme({ commit }, theme) {
      commit('setTheme', theme);
    },
    updateLocale({ commit }, locale) {
      const mappedLocale = mapLocale(locale);
      commit('setLocale', mappedLocale);
    },
    updateLoadDashboard({ commit }, loadDashboard) {
      commit('setLoadDashboard', loadDashboard);
    },
    updateShowWelcomeDialog({ commit }, showWelcomeDialog) {
      commit('setShowWelcomeDialog', showWelcomeDialog);
    },
    updateBuildMode({ commit }, buildMode) {
      commit('setBuildMode', buildMode);
    },
    updateLogoUrl({ commit }, logoUrl) {
      commit('setLogoUrl', logoUrl);
    },
    updateTimeZone({ commit }, timeZone) {
      commit('setTimeZone', timeZone);
    },
    updateShowOpenItemsOnly({ commit }, mode) {
      commit('setShowOpenItemsOnly', mode);
    },
    initializeFilterItemsContext({ commit }, items) {
      commit('initializeFilterItemsContext', items);
    },
    updateFilter({ state }, { visualId, filterSQLCondition }) {
      if (state.filterItemsContext) {
        state.filterItemsContext.updateFilter(visualId, filterSQLCondition);
      }
    },
    
    removeFilter({ state }, visualId) {
      if (state.filterItemsContext) {
        state.filterItemsContext.removeFilter(visualId);
      }
    },
    
    resetFilters({ state }) {
      if (state.filterItemsContext) {
        state.filterItemsContext.resetFilters();
      }
    },
  },
  getters: {
    getDuckDBService: (state) => state.duckDBService,
    isDuckDBInitialized: (state) => state.isDuckDBInitialized,
    getDataLoaded: (state) => state.isDataLoaded,
    getTasksAvailable: (state) => state.tasksAvailable,
    getDBConnection: (state) => state.duckDBService.conn,
    getLoadDashboard: (state) => state.loadDashboard,
    getShowWelcomeDialog: (state) => state.showWelcomeDialog,
    getSettings: (state) => state.settings,
    getApiClient: (state) => state.apiClient,
    getBuildMode: (state) => state.buildMode,
    getShowOpenItemsOnly: (state) => state.settings.showOpenItemsOnly,
    getItems: (state) => state.static.items,
    getCategories: (state) => state.static.categories,
    getCategoriesObj: (state) => state.static.categoriesObj,
    getItemTemplatesObj: (state) => state.static.itemTemplatesObj,
    getStatusTemplatesObj: (state) => state.static.statusTemplatesObj,
    getFormTemplatesObj: (state) => state.static.formTemplatesObj,
    getDrawings: (state) => state.static.drawings,
    getDrawingsObj: (state) => state.static.drawingsObj,
    getDrawingLocationsObj: (state) => state.static.drawingLocationsObj,
    getLocationsObj: (state) => state.static.locationsObj,
    getTeamsObj: (state) => state.static.teamsObj,
    getTeamUsersObj: (state) => state.static.teamUsersObj,
    getUserTeamsObj: (state) => state.static.userTeamsObj,
    getUsersObj: (state) => state.static.usersObj,
    getPermissionRulesObj: (state) => state.static.permissionRulesObj,
    getSelectedItems: (state) => state.selectedItems,
    getSelectedTeam: (state) => state.selectedTeam,
    getStatusFilters: (state) => state.statusFilters,
    getItemTypeFilters: (state) => state.itemTypeFilters,
    getApiUrl: (state) => state.settings.apiUrl,
    getLogoUrl: (state) => state.settings.logoUrl,
    getProjectCode: (state) => state.settings.projectCode,
    getProjectName: (state) => state.settings.projectName,
    getScopedToken: (state) => state.settings.scopedToken,
    getUrl: (state) => state.settings.apiUrl,
    getUsername: (state) => state.settings.username,
    getPassword: (state) => state.settings.password,
    getSelectedProject: (state) => state.settings.projectCode,
    getUserId: (state) => state.settings.userId,
    currentLocale: (state) => state.settings.locale,
    chartFontColor: (state) => {
      return state.theme === 'light' ? '#000000' : '#FFFFFF';
    },
    getFilteredItems: () => {
      return [];
    },
    executeFilteredQuery: (state) => async (selectQuery) => {
      if (state.filterItemsContext) {
        return await state.filterItemsContext.executeFilteredQuery(selectQuery);
      }
      return null;
    },
  },
});

export default store;